import { Transaction } from '@near-wallet-selector/core';
import Decimal from 'decimal.js';
import JSBI from 'jsbi';

import { Provider } from 'near-api-js/lib/providers';
import { DEFAULT_GAS, MEMO, WRAPPED_NEAR_ID } from '../../constants';
import { buildWithdrawNearTransaction } from '../../utils';
import { callFunction } from '../../utils/rpc';
import {
  Amm,
  CreateSwapRouteInstructionsParams,
  Quote,
  QuoteParams,
  SwapMode,
  SwapParams,
} from '../common/types';
import { SPIN_CONTRACT_ADDRESS } from './config';
import {
  LoadUserDepositsParams,
  SpinDeposits,
  SpinMarketOrderbookResponse,
  SpinMarketResponse,
} from './types';

const NATIVE_NEAR_ADDRESS = 'near.near';

export class SpinFinanceMarket implements Amm {
  label = 'Spin Finance';
  id: string;
  reserveTokenMints: string[];
  contractId = SPIN_CONTRACT_ADDRESS;
  instanceId: number;
  private orderBook: SpinMarketOrderbookResponse | undefined = undefined;

  constructor(readonly rawMarket: SpinMarketResponse) {
    this.id = `${rawMarket.id}`;
    this.instanceId = rawMarket.id;
    // Pass wrapped near to the router
    this.reserveTokenMints = [
      rawMarket.base.address,
      rawMarket.quote.address,
    ].map((_) => (_ === NATIVE_NEAR_ADDRESS ? WRAPPED_NEAR_ID : _));
  }

  static async loadUserDeposits({
    user,
    provider,
  }: LoadUserDepositsParams): Promise<SpinDeposits> {
    const deposits = await callFunction<SpinDeposits>({
      provider,
      accountId: SPIN_CONTRACT_ADDRESS,
      method: 'get_deposits',
      args: {
        account_id: user,
      },
    });

    return deposits;
  }

  static createWithdrawFromDepositsTransaction({
    user,
    token,
    amount,
  }: {
    user: string;
    token: string;
    amount: string;
  }): Transaction {
    const isNearToken = token === NATIVE_NEAR_ADDRESS;

    const transaction = {
      receiverId: SPIN_CONTRACT_ADDRESS,
      signerId: user,
      actions: [
        {
          type: 'FunctionCall',
          params: {
            methodName: 'withdraw',
            args: {
              token: isNearToken ? NATIVE_NEAR_ADDRESS : token,
              amount,
            },
            gas: DEFAULT_GAS,
            deposit: isNearToken ? '0' : '1',
          },
        },
      ],
    };

    // @ts-ignore
    return transaction;
  }

  async createSwapInstructions(swapParams: SwapParams): Promise<Transaction[]> {
    const { user, swapStep, provider } = swapParams;

    const transactions: Transaction[] = [];

    const deposits = await SpinFinanceMarket.loadUserDeposits({
      user,
      provider,
    });

    const inputDepositToken =
      swapStep.inputMint === WRAPPED_NEAR_ID
        ? NATIVE_NEAR_ADDRESS
        : swapStep.inputMint;

    const existingAmount = JSBI.BigInt(deposits[inputDepositToken] || '0');

    const { address: baseAddress } = this.rawMarket.base;
    const { address: quoteAddress } = this.rawMarket.quote;

    const isSellBase =
      baseAddress === swapStep.inputMint ||
      (baseAddress === NATIVE_NEAR_ADDRESS &&
        swapStep.inputMint === WRAPPED_NEAR_ID);

    const isNativeMarket =
      baseAddress === NATIVE_NEAR_ADDRESS ||
      quoteAddress === NATIVE_NEAR_ADDRESS;

    // Deposit if not enough
    if (JSBI.lessThan(existingAmount, swapStep.amountIn)) {
      const amountToAdd = JSBI.subtract(swapStep.amountIn, existingAmount);
      // Deposit native NEAR
      if (isNativeMarket && swapStep.inputMint === WRAPPED_NEAR_ID) {
        // Unwrap wrapped NEAR
        transactions.push(
          await buildWithdrawNearTransaction(user, amountToAdd)
        );
        // Deposit near
        transactions.push({
          receiverId: SPIN_CONTRACT_ADDRESS,
          signerId: user,
          actions: [
            {
              type: 'FunctionCall',
              params: {
                methodName: 'deposit_near',
                args: {},
                gas: DEFAULT_GAS,
                deposit: amountToAdd.toString(),
              },
            },
          ],
        });
      } else {
        // Deposit FT
        transactions.push({
          receiverId: swapStep.inputMint,
          signerId: user,
          actions: [
            {
              type: 'FunctionCall',
              params: {
                methodName: 'ft_transfer_call',
                args: {
                  receiver_id: SPIN_CONTRACT_ADDRESS,
                  amount: amountToAdd.toString(),
                  msg: '',
                },
                gas: DEFAULT_GAS,
                deposit: '1',
              },
            },
          ],
        });
      }
    }

    const methodName = isSellBase ? 'place_ask' : 'place_bid';

    const quoteNominator = 10 ** this.rawMarket.quote.decimal;

    const orderStep = new Decimal(this.rawMarket.limits.step_size);
    let quantity = new Decimal(
      (isSellBase ? swapStep.amountIn : swapStep.minAmountOut).toString()
    )
      .div(orderStep)
      .floor()
      .mul(orderStep);

    // if we buy base then quantity is what we'll receive, but spin fi
    // charge fee from amount out, that's why we need to add it here
    if (!isSellBase) {
      quantity = quantity
        .add(swapStep.feeAmount.toString())
        .div(orderStep)
        .ceil()
        .mul(orderStep);
    }

    const quoteAmount = new Decimal(
      (isSellBase ? swapStep.minAmountOut : swapStep.amountIn).toString()
    ).div(quoteNominator);

    const tickSize = new Decimal(this.rawMarket.limits.tick_size);
    const price = quoteAmount
      .mul(quoteNominator)
      .div(tickSize)
      .div(quantity.div(10 ** this.rawMarket.base.decimal))
      .floor()
      .mul(tickSize);

    // Place market order
    transactions.push({
      receiverId: SPIN_CONTRACT_ADDRESS,
      signerId: user,
      actions: [
        {
          type: 'FunctionCall',
          params: {
            methodName,
            args: {
              market_id: this.rawMarket.id,
              price: price.ceil().toFixed(0),
              quantity: quantity.toFixed(0),
              market_order: true,
              memo: MEMO,
            },
            gas: DEFAULT_GAS,
            deposit: '0',
          },
        },
      ],
    });

    const withdrawAmount = swapStep.minAmountOut.toString();
    const withdrawToken =
      swapStep.outputMint === WRAPPED_NEAR_ID
        ? NATIVE_NEAR_ADDRESS
        : swapStep.outputMint;

    // Withdraw
    transactions.push(
      SpinFinanceMarket.createWithdrawFromDepositsTransaction({
        user,
        token: withdrawToken,
        amount: withdrawAmount,
      })
    );

    if (isNativeMarket && swapStep.outputMint === WRAPPED_NEAR_ID) {
      // Wrap NEAR back
      transactions.push({
        receiverId: WRAPPED_NEAR_ID,
        signerId: user,
        actions: [
          {
            type: 'FunctionCall',
            params: {
              methodName: 'near_deposit',
              args: {},
              gas: DEFAULT_GAS,
              deposit: withdrawAmount,
            },
          },
        ],
      });
    }
    return transactions;
  }

  async createSwapRouteInstructions(
    swapParams: CreateSwapRouteInstructionsParams
  ): Promise<Transaction[]> {
    const { provider, user, swapRoute } = swapParams;

    const transactions = [];

    for (let routeStep of swapRoute.steps) {
      const stepTransactions = await routeStep.amm.createSwapInstructions({
        provider,
        user,
        swapStep: routeStep,
      });

      transactions.push(...stepTransactions);
    }

    return transactions;
  }

  async getPromiseForUpdate({
    provider,
  }: {
    provider: Provider;
  }): Promise<void> {
    try {
      const orderBook = await callFunction<SpinMarketOrderbookResponse>({
        provider,
        accountId: SPIN_CONTRACT_ADDRESS,
        method: 'get_orderbook',
        args: {
          market_id: this.rawMarket.id,
          limit: 100,
        },
      });
      this.orderBook = orderBook;
    } catch (e) {
      console.log(
        'error getPromiseForUpdate spin for market',
        this.rawMarket.id
      );
    }
  }

  getQuote(quoteParams: QuoteParams): Quote {
    if (!this.orderBook) {
      throw new Error(
        `Order book for market ${this.rawMarket.ticker} is not loaded`
      );
    }

    const isSellBase =
      quoteParams.inputMint === this.rawMarket.base.address ||
      (quoteParams.inputMint === WRAPPED_NEAR_ID &&
        this.rawMarket.base.address === NATIVE_NEAR_ADDRESS);

    const side = isSellBase
      ? this.orderBook.bid_orders
      : this.orderBook.ask_orders;

    const isAmountInBase =
      (isSellBase && quoteParams.swapMode === SwapMode.ExactIn) ||
      (!isSellBase && quoteParams.swapMode === SwapMode.ExactOut);

    let restUserAmount = new Decimal(quoteParams.amount.toString());

    let amountIn = new Decimal(0);
    let amountOut = new Decimal(0);

    const baseMultiplier = new Decimal(10).pow(this.rawMarket.base.decimal);
    const stepSize = new Decimal(this.rawMarket.limits.step_size);

    const fee = new Decimal(this.rawMarket.fees.taker_fee).div(
      10 ** this.rawMarket.fees.decimals
    );

    for (const order of side) {
      const price = new Decimal(order.price);

      const restAmountInBase = isAmountInBase
        ? restUserAmount.div(stepSize).floor().mul(stepSize)
        : restUserAmount
            .mul(baseMultiplier)
            .div(price)
            .div(stepSize)
            .floor()
            .mul(stepSize);

      const orderAmount = new Decimal(order.quantity);
      const takeFromLevelInBase = Decimal.min(restAmountInBase, orderAmount);

      const takeFromLevelInQuote = takeFromLevelInBase
        .mul(price)
        .div(baseMultiplier);

      amountIn = isSellBase
        ? amountIn.add(takeFromLevelInBase)
        : amountIn.add(takeFromLevelInQuote);

      amountOut = isSellBase
        ? amountOut.add(takeFromLevelInQuote)
        : amountOut.add(takeFromLevelInBase);

      const feeAmount = amountOut.mul(fee);

      const amountOutLessFee = amountOut.sub(feeAmount).floor();

      let minAmountOut = new Decimal(
        quoteParams.slippage
          .getFor(JSBI.BigInt(amountOutLessFee.floor().toString()))
          .toString()
      );

      // after removing slippage part requires to be stripped
      if (!isSellBase) {
        minAmountOut = minAmountOut.div(stepSize).floor().mul(stepSize);
      }

      restUserAmount = restUserAmount.sub(
        isAmountInBase ? takeFromLevelInBase : takeFromLevelInQuote
      );

      if (takeFromLevelInBase.lessThanOrEqualTo(0)) {
        const amountInBase = isSellBase ? amountIn : minAmountOut;
        const amountInQuote = isSellBase ? minAmountOut : amountIn;

        // check market limits for base token
        const isAmountInBaseGreaterMinLimit = amountInBase.greaterThanOrEqualTo(
          new Decimal(this.rawMarket.limits.min_base_quantity)
        );
        const isAmountInBaseLessMaxLimit = amountInBase.lessThanOrEqualTo(
          new Decimal(this.rawMarket.limits.max_base_quantity)
        );

        // check market limits for quote token
        const isAmountInQuoteGreaterMinLimit =
          amountInQuote.greaterThanOrEqualTo(
            new Decimal(this.rawMarket.limits.min_quote_quantity)
          );

        const isAmountInQuoteLessMaxLimit = amountInQuote.lessThanOrEqualTo(
          new Decimal(this.rawMarket.limits.max_quote_quantity)
        );

        return {
          amountIn: JSBI.BigInt(amountIn.floor().toString()),
          amountOut: JSBI.BigInt(amountOutLessFee.floor().toString()),
          minAmountOut: JSBI.BigInt(minAmountOut.floor().toString()),
          feeAmount: JSBI.BigInt(feeAmount.floor().toString()),
          feeMint: quoteParams.outputMint,
          feePct:
            parseFloat(this.rawMarket.fees.taker_fee) /
            10 ** this.rawMarket.base.decimal,
          notEnoughLiquidity: !(
            isAmountInBaseGreaterMinLimit &&
            isAmountInBaseLessMaxLimit &&
            isAmountInQuoteGreaterMinLimit &&
            isAmountInQuoteLessMaxLimit
          ),
          priceImpact: 0,
        };
      }
    }

    const feeAmount = amountOut.mul(fee);
    return {
      amountIn: JSBI.BigInt(amountIn.floor().toString()),
      amountOut: JSBI.BigInt(amountOut.sub(feeAmount).floor().toString()),
      minAmountOut: quoteParams.slippage.getFor(
        JSBI.BigInt(amountOut.floor().toString())
      ),
      feeAmount: JSBI.BigInt(feeAmount.floor().toString()),
      feeMint: quoteParams.inputMint,
      feePct:
        parseFloat(this.rawMarket.fees.taker_fee) /
        10 ** this.rawMarket.base.decimal,
      notEnoughLiquidity: true,
      priceImpact: 0,
    };
  }

  static async loadMarkets(params: { provider: Provider }) {
    const { provider } = params;
    const markets = await callFunction<SpinMarketResponse[]>({
      accountId: SPIN_CONTRACT_ADDRESS,
      method: 'get_markets',
      provider,
    });
    return markets.map((market) => new SpinFinanceMarket(market));
  }
}
